import { motion } from "framer-motion";
import { Mail, Phone, Linkedin, Calendar, Download, ArrowUpRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { PROFILE } from "../../data/content";

const CONTACTS = [
  { key: "email", label: "Email", value: PROFILE.email, href: `mailto:${PROFILE.email}`, Icon: Mail },
  { key: "phone", label: "Phone", value: PROFILE.phone, note: "Text friendly", href: PROFILE.phoneHref, Icon: Phone },
  { key: "calendly", label: "Book a Call", value: "calendly.com/jessicadannielle", note: "Schedule instantly", href: "https://calendly.com/jessicadannielle", Icon: Calendar, external: true },
  { key: "linkedin", label: "LinkedIn", value: "in/jessicadannielle", href: PROFILE.linkedin, Icon: Linkedin, external: true },
];

export const Contact = () => (
  <footer id="contact" data-testid="contact-section" className="bg-ink text-paper">
    <div className="mx-auto max-w-[1500px] px-6 md:px-12 py-24 md:py-40">
      <Reveal>
        <p className="overline text-terracotta">(06) — Let's Talk</p>
        <h2 className="mt-6 font-serif tracking-tight leading-[0.9] text-[13vw] md:text-[8.5rem]">
          Let's build
          <br />
          <span className="italic text-terracotta">something.</span>
        </h2>
      </Reveal>

      <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 rounded-2xl overflow-hidden">
        {CONTACTS.map(({ key, label, value, note, href, Icon, external }, i) => (
          <Reveal key={key} delay={i * 0.08}>
            <a
              href={href}
              data-testid={`contact-${key}-link`}
              {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
              className="group flex h-full flex-col justify-between gap-10 bg-ink p-8 md:p-10 hover:bg-terracotta transition-colors duration-300"
            >
              <div className="flex items-center justify-between">
                <Icon size={26} className="text-paper" />
                <ArrowUpRight
                  size={22}
                  className="text-white/40 group-hover:text-paper group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-300"
                />
              </div>
              <div>
                <p className="overline text-white/50 group-hover:text-paper/80 transition-colors">{label}</p>
                <p className="mt-2 font-serif text-2xl md:text-[1.7rem] tracking-tight break-words leading-tight">
                  {value}
                </p>
                {note && (
                  <p className="mt-1.5 text-[0.65rem] uppercase tracking-[0.16em] text-white/40 group-hover:text-paper/70 transition-colors">
                    {note}
                  </p>
                )}
              </div>
            </a>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.1}>
        <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-6">
          <a
            href={PROFILE.resume}
            download
            data-testid="contact-resume-download"
            className="group inline-flex items-center gap-3 rounded-full bg-paper text-ink pl-7 pr-3 py-3 text-sm uppercase tracking-[0.14em] hover:bg-sage hover:text-paper transition-colors duration-300"
          >
            Download Résumé
            <span className="grid place-items-center w-8 h-8 rounded-full bg-ink text-paper group-hover:translate-y-0.5 transition-transform duration-300">
              <Download size={15} />
            </span>
          </a>
          <p className="text-white/50 text-sm max-w-md">
            Based in {PROFILE.location}. Open to both contract and permanent salaried roles. Remote
            work preferred, willing to travel onsite once per month if necessary.
          </p>
        </div>
      </Reveal>

      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1 }}
        className="mt-24 pt-10 border-t border-white/10 flex flex-col md:flex-row justify-between gap-4 text-sm text-white/40"
      >
        <span className="font-serif text-lg text-paper">{PROFILE.name}</span>
        <span>© {new Date().getFullYear()} — Instructional Design · Learning Experience · Creative</span>
      </motion.div>

      <p
        data-testid="references-note"
        className="mt-10 text-center text-xs md:text-sm uppercase tracking-[0.2em] text-white/40"
      >
        Professional References available upon request
      </p>
    </div>
  </footer>
);
